/**
 * Canonical sponsor-task status / completion / compensation helpers.
 *
 * All deadline/cure period arithmetic lives in @/utils/deadline.
 * This module consumes those helpers and adds status resolution logic
 * shared across student screens, admin screens, and (later) the backend.
 *
 * `queue_job_status === 'ready'` means a proof was stored during the cure
 * period and awaits its final check (the cure-end auto-check scheduler).
 * Such tasks are treated as 'pending' everywhere and are NOT compensated
 * until the auto-check resolves them.
 */
import { parseLocalDate, getActivityDeadline } from "@/utils/deadline";

// ─── Task-level helpers ─────────────────────────────────────

/** Whether the task's queue_job_status is 'ready' (proof stored during cure, awaiting final check). */
export function isTaskReady(c: any): boolean {
  return c?.queue_job_status === "ready";
}

/**
 * Check if a task completion record has non-empty proof.
 *
 * Delegates to the proof-shape helpers rather than sniffing two keys inline: the
 * inline version ignored `registered_usernames`, so a `register_app` proof was
 * reported as "no proof" and its submission rendered as blank.
 */
export function hasProof(c: any): boolean {
  return hasAnySponsorProof(c?.proof);
}

/** Whether a task is considered "completed" (by completed flag or queue_job_status). */
export function isTaskCompleted(c: any): boolean {
  return c.completed === true || c.queue_job_status === "completed";
}

/**
 * Extract the proof array from various proof formats.
 * Handles: string[], { ig_accounts: [] }, { registered_usernames: [] },
 * { media: [] }, JSON strings, null/undefined.
 */
export function extractProofArray(proof: any): any[] {
  return getSponsorProofEntries(proof);
}

// ─── Proof shape ─────────────────────────────────────────────

/**
 * The single place that knows what a sponsor-task `proof` column can contain.
 *
 * A proof is stored as JSON and has been written in several shapes over time:
 *
 *   - a bare array                     → `["@a", "@b"]`
 *   - `{ ig_accounts: [...] }`         → ensure_followers
 *   - `{ registered_usernames: [...] }`→ register_app
 *   - `{ media: [{ url, ... }] }`      → media proof tasks
 *   - the whole thing as a JSON string  → rows read straight out of MySQL
 *
 * Screens used to each hand-roll this, so a proof written by one flow was
 * invisible to the screen that had to display it. Every call site now goes
 * through these three functions instead.
 *
 * @param key which payload key to read
 * @returns the entries under that key, or the bare array if the proof is one
 */
function proofEntriesForKey(proof: unknown, key: string): any[] {
  if (Array.isArray(proof)) return proof;
  if (typeof proof === "string") {
    try {
      return proofEntriesForKey(JSON.parse(proof), key);
    } catch {
      return [];
    }
  }
  if (typeof proof === "object" && proof !== null) {
    const own = (proof as Record<string, unknown>)[key];
    if (Array.isArray(own)) return own;
  }
  return [];
}

/** Trim and drop non-string entries from a proof payload. */
function toStringList(entries: any[]): string[] {
  return entries
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter((entry) => entry !== "");
}

/** IG account handles submitted for an `ensure_followers` task. */
export function getSponsorProofIgAccounts(proof: unknown): string[] {
  return toStringList(proofEntriesForKey(proof, "ig_accounts"));
}

/** App usernames submitted for a `register_app` task. */
export function getSponsorProofRegisteredUsernames(proof: unknown): string[] {
  return toStringList(proofEntriesForKey(proof, "registered_usernames"));
}

/** Media entries (each with a `url`) submitted for a media proof task. */
export function getSponsorProofMedia(proof: unknown): any[] {
  return Array.isArray(proof) ? [] : proofEntriesForKey(proof, "media");
}

/**
 * Every entry in a proof, whichever key it was stored under.
 *
 * Order matters: a proof is written under exactly one key, so the first key that
 * is present wins. `registered_usernames` is checked too — leaving it out is what
 * made `register_app` submissions invisible to the generic proof readers.
 */
export function getSponsorProofEntries(proof: unknown): any[] {
  for (const key of ["ig_accounts", "registered_usernames", "media"]) {
    const entries = proofEntriesForKey(proof, key);
    if (entries.length > 0) return entries;
  }
  return [];
}

/** Whether a proof payload carries any entry at all. */
export function hasAnySponsorProof(proof: unknown): boolean {
  if (proof === null || proof === undefined || proof === "") return false;
  if (Array.isArray(proof)) return proof.length > 0;
  if (typeof proof === "string") {
    // A JSON string that does not parse is not a proof we can read.
    return getSponsorProofEntries(proof).length > 0;
  }
  if (typeof proof === "object") return getSponsorProofEntries(proof).length > 0;
  return false;
}

/**
 * Resolve the effective job status for a task.
 *
 * Evaluation order:
 *   1. isTaskCompleted → 'completed'
 *   2. review_count ≥ 3 → 'failed'
 *   3. override (optimistic state) → override value
 *   4. task.queue_job_status → that value
 *   5. fallback → 'pending'
 */
export function getEffectiveJobStatus(task: any, override?: string): string {
  if (isTaskCompleted(task)) return "completed";
  if (typeof task.review_count === "number" && task.review_count >= 3)
    return "failed";
  if (override) return override;
  return task.queue_job_status || "pending";
}

/** Check if any task in the list has proof with utime ≤ deadlineEnd. */
export function getActivityHasPreDeadlineProof(
  tasks: any[],
  deadlineEnd: Date | null,
): boolean {
  if (!deadlineEnd || !Array.isArray(tasks)) return false;
  return tasks.some((task) => {
    if (!task.proof || !task.utime) return false;
    const proofArr = extractProofArray(task.proof);
    if (!proofArr.length) return false;
    const proofTime = new Date(task.utime);
    return !isNaN(proofTime.getTime()) && proofTime <= deadlineEnd;
  });
}

/**
 * Whether a task is finally failed: not completed, and either maxed out
 * review attempts (≥ 3) or the attempt window (deadline/cure) has closed.
 *
 * A `queue_job_status === 'ready'` task (proof stored during the cure period,
 * awaiting the cure-end auto-check) is never finally failed — it must not be
 * auto-failed merely because the cure clock reached zero.
 */
export function isTaskFinallyFailed(
  tasks: any[],
  c: any,
  deadlineStr: string | null,
  now: Date = new Date(),
): boolean {
  if (isTaskCompleted(c)) return false;
  if (c.queue_job_status === "ready") return false;
  if (typeof c.review_count === "number" && c.review_count >= 3) return true;
  return !canAttemptActivity(tasks, deadlineStr, now);
}

/**
 * Whether a bot-rejected task is still retryable within the open attempt window.
 */
export function isTaskRetryable(
  tasks: any[],
  c: any,
  deadlineStr: string | null,
  now: Date = new Date(),
): boolean {
  if (isTaskCompleted(c)) return false;
  if (typeof c.review_count === "number" && c.review_count >= 3) return false;
  if (!canAttemptActivity(tasks, deadlineStr, now)) return false;
  return getEffectiveJobStatus(c) === "failed";
}

/** Check if any task is failed (not completed) with max review count (≥ 3). */
export function anyTaskFailedMaxReview(tasks: any[]): boolean {
  return tasks.some(
    (task) =>
      !isTaskCompleted(task) &&
      typeof task.review_count === "number" &&
      task.review_count >= 3 &&
      getEffectiveJobStatus(task) === "failed",
  );
}

// ─── Activity-level helpers ─────────────────────────────────

/**
 * Compute completion status for a single task with full context.
 *
 * A `queue_job_status === 'failed'` (bot-rejected) task is 'pending'
 * (retryable) while the attempt window is open; it only becomes 'failed'
 * once the window closes (deadline passed with no pre-deadline proof, or
 * cure period expired) or when review_count ≥ 3.
 *
 * A `queue_job_status === 'ready'` task (proof stored during the cure period,
 * awaiting the cure-end auto-check) is always 'pending' — it must display as
 * "Awaiting check" and must never be auto-failed merely by the cure clock
 * reaching zero.
 *
 * @param c                   – task completion record
 * @param hasPreDeadlineProof – activity-level: any task has proof with utime ≤ deadline
 * @param deadlineEnd         – deadline date at 23:59:59.999 local (or null)
 * @param cureEnd             – deadlineEnd + 7 days (or null)
 * @param now                 – current time (injectable for testing)
 */
export function getCompletionStatus(
  c: any,
  hasPreDeadlineProof: boolean,
  deadlineEnd: Date | null,
  cureEnd: Date | null,
  now: Date = new Date(),
): "completed" | "failed" | "pending" {
  if (isTaskCompleted(c)) return "completed";
  if (typeof c.review_count === "number" && c.review_count >= 3) return "failed";
  if (c.queue_job_status === "ready") return "pending";

  // Past deadline: check cure eligibility
  if (deadlineEnd && now > deadlineEnd) {
    if (!hasPreDeadlineProof) return "failed"; // no cure period → auto-fail
    if (cureEnd && now > cureEnd) return "failed"; // cure period expired
  }

  return "pending";
}

/**
 * Compute overall activity status.
 *
 * @param completions  – array of task completion records
 * @param activityDate – the activity_date string (YYYY-MM-DD), used to derive deadline/cure times
 */
export function getActivityStatus(
  completions: any[] | undefined,
  activityDate: string | null,
): {
  status: "completed" | "failed" | "pending";
  total: number;
  completed: number;
  failed: number;
  pending: number;
} {
  const list = completions || [];

  // Derive time boundaries from activityDate
  const deadline = activityDate ? getActivityDeadline(activityDate) : null;
  let deadlineEnd: Date | null = null;
  let cureEnd: Date | null = null;
  if (deadline) {
    deadlineEnd = new Date(deadline.getTime());
    deadlineEnd.setHours(23, 59, 59, 999);
    // Compute cureEnd inline (avoids toISOString off-by-one from getActivityDeadlineString)
    cureEnd = new Date(deadlineEnd.getTime() + 7 * 24 * 60 * 60 * 1000);
  }

  const hasPreDeadlineProof = getActivityHasPreDeadlineProof(list, deadlineEnd);

  let completed = 0;
  let failed = 0;
  let pending = 0;

  for (const c of list) {
    const s = getCompletionStatus(c, hasPreDeadlineProof, deadlineEnd, cureEnd);
    if (s === "completed") completed++;
    else if (s === "failed") failed++;
    else pending++;
  }

  const total = list.length;
  let status: "completed" | "failed" | "pending";
  if (total === 0) status = "pending";
  else if (completed === total) status = "completed";
  else if (failed > 0 && completed + failed === total) status = "failed";
  else status = "pending";

  return { status, total, completed, failed, pending };
}

/**
 * Whether the student can still attempt the activity (within deadline or cure period).
 * Receives the deadline STRING (not activity date); computes deadlineEnd directly
 * to avoid double-counting the activity_date → deadline month offset.
 *
 * Rules:
 *   - The whole pre-deadline period is always allowed.
 *   - After the deadline, the 7-day cure period is only granted if the activity
 *     had at least one proof submitted before the deadline.
 *
 * @param tasks       – list of task completion records (for pre-deadline proof check)
 * @param deadlineStr – deadline as YYYY-MM-DD
 * @param now         – current time (injectable for testing)
 */
export function canAttemptActivity(
  tasks: any[] | undefined,
  deadlineStr: string | null,
  now: Date = new Date(),
): boolean {
  if (!deadlineStr) return true;
  const deadlineDate = parseLocalDate(deadlineStr);
  if (!deadlineDate) return true;
  const deadlineEnd = new Date(deadlineDate.getTime());
  deadlineEnd.setHours(23, 59, 59, 999);
  if (now <= deadlineEnd) return true;
  if (!getActivityHasPreDeadlineProof(tasks || [], deadlineEnd)) return false;
  const cureEnd = new Date(deadlineEnd.getTime() + 7 * 24 * 60 * 60 * 1000);
  return now <= cureEnd;
}

// ─── Compensation ───────────────────────────────────────────

/**
 * Calculate the compensation amount.
 *
 * Counts ALL unresolved tasks at cure end (any task not marked completed,
 * including bot-failed and pending-without-proof tasks).
 *
 * Tasks with `queue_job_status === 'ready'` are treated as pending and are
 * NOT compensated until the cure-end auto-check resolves them.
 *
 * @param tasks      – list of task completion records
 * @param failedDate – date from which daily interest starts (deadline or cure-end)
 */
export function calculateCompensation(
  tasks: any[],
  failedDate: Date | null = null,
): number {
  const failedTasks = tasks.filter((task) => !isTaskCompleted(task));
  const numFailed = failedTasks.length;
  if (numFailed === 0) return 0;

  const base = 3500;
  const interestPerTask = 0.04;
  let compensation = base + numFailed * base * interestPerTask;

  if (failedDate) {
    const now = new Date();
    const daysLate = Math.max(
      0,
      Math.floor(
        (now.getTime() - failedDate.getTime()) / (1000 * 60 * 60 * 24),
      ),
    );
    if (daysLate > 0) {
      compensation += compensation * 0.02 * daysLate;
    }
  }

  return Math.round(compensation);
}